import { AxiosError } from 'axios'
import { v4 as uuid } from 'uuid'

import {
  ConversationV2,
  mapConversationV2ToMessageConversation,
} from '@lyrahealth-inc/shared-app-logic/src/features/messaging/conversations'
import {
  ListChatHistoryResponseV2,
  mapChatHistoryResponseV2ToPage,
  V2_CHAT_HISTORY_PAGE_SIZE,
} from '@lyrahealth-inc/shared-app-logic/src/features/messaging/messagesV2'
import { ConversationObject } from '@lyrahealth-inc/shared-app-logic/src/models/conversation/Conversation'
import { ProviderPairConversationObject } from '@lyrahealth-inc/shared-app-logic/src/models/conversation/CoordinatedCareConversation'
import { AIDraft, AIDraftPromptType } from '@lyrahealth-inc/shared-app-logic/src/models/draft/AIDraft'
import { Draft as DraftObject, DraftStatus } from '@lyrahealth-inc/shared-app-logic/src/models/draft/Draft'
import {
  CONVERSATION_STATE,
  CONVERSATION_TYPE,
  ConversationMessages,
  Message,
} from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'
import type { CareHistoryProvider } from '@lyrahealth-inc/shared-app-logic/src/models/providers/CareHistoryProviders'

import { updateSelectedConversationID } from './messagesSlice'
import { parseProviderPairCareHistoryResponse } from './providerPairCareHistory'
import {
  parseProviderPairConversationListResponse,
  parseProviderPairCreateResponse,
  type ProviderPairCreateResponse,
} from './providerPairUtils'
import { actionAlertHandler, actionStyles } from '../../../common/constants/reduxConstants'
import { getAuthUserId } from '../../../data/auth/authSelectors'
import { baseApi, RTKQueryTag } from '../../../data/baseApi'
import { updateEndpointCache } from '../../../data/rtkQuery'
import { type AppDispatch, RootState } from '../../../data/store'

type GetConversationsArgs = {
  providerId: string
  derivedProperties?: string
  types?: CONVERSATION_TYPE
}
type GetConversationsV2Args = {
  providerId: string
  type?: CONVERSATION_TYPE
}
type GetPatientConversationsArgs = {
  providerId: string
  patientId: string
  types?: CONVERSATION_TYPE
}
type GetPatientConversationsV2Args = {
  providerId: string
  patientId: string
  type?: CONVERSATION_TYPE
}

type GetProviderPairConversationsArgs = {
  providerId: string
  patientId?: string
}

type GetProviderPairCareHistoryArgs = {
  providerId: string
  patientId: string
}

// Query lifecycles include automatic focus/invalidation refreshes, which RTK Query 1.x's
// getRunningQueryThunk does not track. Keep pending pair lists scoped to their store.
const pendingProviderPairLists = new WeakMap<object, Set<Promise<unknown>>>()

const AI_CONVERSATION_TYPES = [CONVERSATION_TYPE.AI_COACH, CONVERSATION_TYPE.MANAGER_COACHING_AI] as const

type BuildConversationsQueryParamsArgs = {
  providerId: string
  patientId?: string
  derivedProperties?: string
  conversationTypes: readonly CONVERSATION_TYPE[]
}

export const getConversationTypesForRequest = (type: CONVERSATION_TYPE): readonly CONVERSATION_TYPE[] =>
  type === CONVERSATION_TYPE.AI_COACH ? AI_CONVERSATION_TYPES : [type]

/**
 * V2 takes `provider_id`/`patient_id` and, like V1 and the member apps, one repeated `type` param
 * per conversation type, so a Lyra AI request also returns manager growth coaching conversations.
 */
export const buildConversationsV2QueryParams = ({
  providerId,
  patientId,
  type,
}: {
  providerId: string
  patientId?: string
  type: CONVERSATION_TYPE
}): URLSearchParams => {
  const params = new URLSearchParams()
  params.append('provider_id', providerId)
  if (patientId !== undefined) {
    params.append('patient_id', patientId)
  }
  getConversationTypesForRequest(type).forEach((conversationType) => params.append('type', conversationType))
  return params
}

export const buildConversationsQueryParams = ({
  providerId,
  patientId,
  derivedProperties,
  conversationTypes,
}: BuildConversationsQueryParamsArgs): URLSearchParams => {
  const params = new URLSearchParams()
  params.append('provider_lyra_id', providerId)
  if (patientId !== undefined) {
    params.append('patient_lyra_id', patientId)
  }
  conversationTypes.forEach((type) => params.append('type', type))
  if (derivedProperties !== undefined) {
    params.append('derived_properties', derivedProperties)
  }
  return params
}

type CreateConversationArgs = {
  episodeId: string
  participants: Array<{ participant_lyra_id: string; type: 'provider' | 'patient' }>
}

export type EnsureProviderPairConversationInput = {
  patientId: string
  targetProviderId: string
}

type EnsureProviderPairConversationArgs = EnsureProviderPairConversationInput & {
  providerId: string
}

export const buildCreateConversationRequestData = ({ episodeId, participants }: CreateConversationArgs) => ({
  episode_id: episodeId,
  participants,
})

// A send to a closed conversation can replace it; the backend reports the new id even when the send
// itself then fails. Accepts either the lifecycle rejection or the unwrapped AxiosError.
export const getReplacementConversationId = (error: unknown): string | undefined => {
  const axiosError = ((error as { error?: unknown } | undefined)?.error ?? error) as AxiosError | undefined
  const body = axiosError?.response?.data as { new_conversation_id?: unknown } | string | null | undefined
  const replacementId = typeof body === 'object' ? body?.new_conversation_id : undefined
  return typeof replacementId === 'string' && replacementId ? replacementId : undefined
}

export const buildProviderPairEnsureRequest = ({
  patientId,
  providerId,
  targetProviderId,
}: EnsureProviderPairConversationArgs) => ({
  method: 'post' as const,
  url: '/lt/v1/messaging/conversations/participants',
  // Supervisors must name the provider they act as; the backend ignores it for everyone else.
  params: { provider_lyra_id: providerId },
  data: {
    patient_lyra_id: patientId,
    participants: [
      { participant_lyra_id: providerId, type: 'provider' },
      { participant_lyra_id: targetProviderId, type: 'provider' },
    ],
  },
})

export const buildProviderPairCareHistoryRequest = ({ providerId, patientId }: GetProviderPairCareHistoryArgs) => ({
  method: 'get' as const,
  url: `/lt/v1/patient/${encodeURIComponent(patientId)}/care-history/providers`,
  // Supervisors must name the provider they act as; the backend ignores it for everyone else.
  params: { provider_id: providerId },
})

// Not a ConversationObject: this endpoint returns the db id as `id` and the messaging id, the one
// every /conversations/{id}/... route takes, as `external_conversation_id`. `created` is false when
// an existing conversation was returned instead of a new one.
type CreateConversationResponse = {
  id: string
  external_conversation_id: string
  type?: CONVERSATION_TYPE
  status?: CONVERSATION_STATE
  episode_id?: string
  created?: boolean
  reinstated?: boolean
}

type GetMessagesArgs = {
  conversationId: string
  cursor?: string
}

type PostMessageArgs = {
  conversationId: string
  conversationType?: CONVERSATION_TYPE
  data: {
    message_body: string
    message_type: string
    message_author_id: string
    message_author_type: string
    metadata?: any
    message_draft_id?: string
  }
}

type GetMessageArgs = {
  conversationId: string
  messageId: string
  append: boolean
}

type PatchConversationAttributesArgs = {
  conversationId: string
  conversationType?: CONVERSATION_TYPE
  isV2?: boolean
  unreadProviderMessagesCount: number
  unreadAiCoachMessagesProviderCount?: number
}

type PostOOOMessageArgs = {
  message_body: string
  message_type: string
  provider_lyra_id: string
  delivery_datetime?: string
  time_zone?: string
}

type GetDraftsArgs = {
  providerId: string
}

type DeleteDraftArgs = {
  conversationId: string
  draftId: string
}

type PatchDraftsArgs = {
  conversationId: string
  draftId: string
  data: {
    body?: string
    meta_data?: any
    status?: DraftStatus
  }
}

type PostDraftArgs = {
  conversationId: string
  data: {
    body: string
    meta_data?: any
    provider_id: string
  }
}

type GetTemplatesArgs = {
  providerId: string
}

type GetAIDraftArgs = {
  providerId: string
  episodeId: string
  sessionNumber: number
  clientId: string
}

type GetAIDraftsBulkArgs = {
  promptType: AIDraftPromptType
  statusOnly: boolean
  providerId: string
}

type PatchAIDraftArgs = {
  providerId: string
  draftId: string
  status: string
}

type PatchConversationArgs = {
  providerId: string
  patientId: string
  conversationId: string
  cacheConversationId?: string
  isV2?: boolean
  status: CONVERSATION_STATE
}

export const mapConversationV2ToProviderConversation = (conversation: ConversationV2): ConversationObject => ({
  ...mapConversationV2ToMessageConversation(conversation),
  conversation_update_datetime: conversation.update_date_utc,
  provider_lyra_id: conversation.provider_id ?? '',
})

const messagesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getConversations: builder.query<ConversationObject[], GetConversationsArgs>({
      query: ({ providerId, derivedProperties, types = CONVERSATION_TYPE.PROVIDER }) => ({
        method: 'get',
        url: `/lt/v1/messaging/conversations`,
        params: buildConversationsQueryParams({
          providerId,
          derivedProperties,
          conversationTypes: getConversationTypesForRequest(types),
        }),
      }),
      providesTags: [RTKQueryTag.CONVERSATION, RTKQueryTag.PERSIST],
    }),
    getPatientConversations: builder.query<ConversationObject[], GetPatientConversationsArgs>({
      query: ({ providerId, patientId, types = CONVERSATION_TYPE.PROVIDER }) => ({
        method: 'get',
        url: `/lt/v1/messaging/conversations`,
        params: buildConversationsQueryParams({
          providerId,
          patientId,
          conversationTypes: getConversationTypesForRequest(types),
        }),
      }),
      providesTags: [RTKQueryTag.CONVERSATION],
      async onQueryStarted({ providerId, types = CONVERSATION_TYPE.PROVIDER }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(
            updateEndpointCache(messagesApi, 'getConversations')
              .where({ providerId, types })
              .build((conversations) => {
                data.forEach((patientConversation) => {
                  const conversationToUpdateIdx = conversations.findIndex(
                    (conversation) => conversation.conversation_id === patientConversation.conversation_id,
                  )
                  if (conversationToUpdateIdx !== -1) {
                    conversations[conversationToUpdateIdx] = patientConversation
                  } else {
                    conversations.push(patientConversation)
                  }
                })
              }),
          )
        } catch {}
      },
    }),
    getConversationsV2: builder.query<ConversationObject[], GetConversationsV2Args>({
      query: ({ providerId, type = CONVERSATION_TYPE.AI_COACH }) => ({
        method: 'get',
        url: `/lt/v2/messaging/conversations`,
        params: buildConversationsV2QueryParams({ providerId, type }),
      }),
      transformResponse: (response: ConversationV2[]) => response.map(mapConversationV2ToProviderConversation),
      providesTags: [RTKQueryTag.CONVERSATION, RTKQueryTag.PERSIST],
    }),
    getPatientConversationsV2: builder.query<ConversationObject[], GetPatientConversationsV2Args>({
      query: ({ providerId, patientId, type = CONVERSATION_TYPE.AI_COACH }) => ({
        method: 'get',
        url: `/lt/v2/messaging/conversations`,
        params: buildConversationsV2QueryParams({ providerId, patientId, type }),
      }),
      transformResponse: (response: ConversationV2[]) => response.map(mapConversationV2ToProviderConversation),
      providesTags: [RTKQueryTag.CONVERSATION],
      async onQueryStarted({ providerId, type = CONVERSATION_TYPE.AI_COACH }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(
            updateEndpointCache(messagesApi, 'getConversationsV2')
              .where({ providerId, type })
              .build((conversations) => {
                data.forEach((patientConversation) => {
                  const conversationToUpdateIdx = conversations.findIndex(
                    (conversation) => conversation.conversation_id === patientConversation.conversation_id,
                  )
                  if (conversationToUpdateIdx !== -1) {
                    conversations[conversationToUpdateIdx] = patientConversation
                  } else {
                    conversations.push(patientConversation)
                  }
                })
              }),
          )
        } catch {}
      },
    }),
    getProviderPairConversations: builder.query<ProviderPairConversationObject[], GetProviderPairConversationsArgs>({
      query: ({ providerId, patientId }) => ({
        method: 'get',
        url: `/lt/v1/messaging/conversations`,
        showAlert: false,
        params: buildConversationsQueryParams({
          providerId,
          patientId,
          conversationTypes: [CONVERSATION_TYPE.PROVIDER_PROVIDER],
        }),
      }),
      transformResponse: parseProviderPairConversationListResponse,
      providesTags: [RTKQueryTag.PROVIDER_PAIR_CONVERSATION],
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        const pending = pendingProviderPairLists.get(dispatch) ?? new Set<Promise<unknown>>()
        pendingProviderPairLists.set(dispatch, pending)
        pending.add(queryFulfilled)
        try {
          await queryFulfilled
        } catch {
          // A failed list must still release sends waiting to refresh its state.
        } finally {
          pending.delete(queryFulfilled)
        }
      },
    }),
    getProviderPairCareHistory: builder.query<CareHistoryProvider[], GetProviderPairCareHistoryArgs>({
      query: buildProviderPairCareHistoryRequest,
      transformResponse: parseProviderPairCareHistoryResponse,
    }),
    getAccessToken: builder.query<string, void>({
      query: () => ({
        method: 'get',
        url: `/lt/v1/messaging/accessToken`,
      }),
    }),
    getMessages: builder.query<ConversationMessages, GetMessagesArgs>({
      query: ({ conversationId, cursor }) => ({
        method: 'get',
        url: `/lt/v1/messaging/conversations/${conversationId}/messages`,
        params: { page_token: cursor, page_size: 50 },
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        return queryArgs.conversationId
      },
      merge: (currentCache, newItems, { arg: { cursor } }) => {
        // If there's no cursor, we are refetching and should replace the cache
        if (!cursor) {
          return newItems
        }
        return {
          conversation_messages: [...newItems.conversation_messages, ...currentCache.conversation_messages],
          next_page_token: newItems.next_page_token,
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.conversationId !== previousArg?.conversationId
      },
    }),
    getMessagesV2: builder.query<ConversationMessages, GetMessagesArgs>({
      query: ({ conversationId, cursor }) => ({
        method: 'get',
        url: `/lt/v2/messaging/conversations/${conversationId}/messages`,
        params: { limit: V2_CHAT_HISTORY_PAGE_SIZE, before: cursor },
      }),
      transformResponse: (response: ListChatHistoryResponseV2, _meta, { conversationId }) => {
        const page = mapChatHistoryResponseV2ToPage(response, conversationId)
        return {
          conversation_messages: page.messages,
          next_page_token: page.hasMore ? page.before : null,
        }
      },
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (reason) {
          if (reason.isUnhandledError) {
            actionAlertHandler({
              actionStyle: actionStyles.ERROR,
              message: 'Unable to load message history. Please try again.',
              dispatch,
            })
          }
        }
      },
      serializeQueryArgs: ({ queryArgs }) => queryArgs.conversationId,
      merge: (currentCache, newItems, { arg: { cursor } }) => {
        if (!cursor) {
          return newItems
        }

        // Ignore duplicate or stale pagination responses. The cursor used for
        // the request must still be the cursor exposed by the current cache.
        if (currentCache.next_page_token !== cursor) {
          return currentCache
        }

        const seenMessageIds = new Set<string>()
        const conversationMessages = [...newItems.conversation_messages, ...currentCache.conversation_messages].filter(
          (message) => {
            if (seenMessageIds.has(message.message_id)) {
              return false
            }
            seenMessageIds.add(message.message_id)
            return true
          },
        )
        const nextPageToken = newItems.next_page_token === cursor ? null : newItems.next_page_token

        return {
          conversation_messages: conversationMessages,
          next_page_token: nextPageToken,
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.conversationId !== previousArg?.conversationId || currentArg?.cursor !== previousArg?.cursor
      },
    }),
    getLatestMessage: builder.query<ConversationMessages, { conversationId: string }>({
      query: ({ conversationId }) => ({
        method: 'get',
        url: `/lt/v1/messaging/conversations/${conversationId}/messages`,
        params: { page_size: 1 },
      }),
      providesTags: [RTKQueryTag.CONVERSATION],
    }),
    sendMessage: builder.mutation<Message & { new_conversation_id?: string }, PostMessageArgs>({
      query: ({ conversationId, data }) => ({
        method: 'post',
        url: `/lt/v1/messaging/conversations/${conversationId}/messages`,
        data,
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled, getState }) {
        const tempId = uuid()
        let refreshPairList = args.conversationType === CONVERSATION_TYPE.PROVIDER_PROVIDER
        // Points caches and selection at a conversation the backend replaced (a closed transport).
        const repointConversation = (oldConversationId: string, newConversationId: string, messageSent: boolean) => {
          const providerId = getAuthUserId(getState() as RootState)
          const conversationUpdater = (conversation: ConversationObject) => {
            conversation.conversation_id = newConversationId
            conversation.conversation_state = CONVERSATION_STATE.ACTIVE
            if (messageSent) {
              conversation.conversation_last_message_sent_datetime = new Date().toISOString()
            }
          }
          dispatch(
            updateEndpointCache(messagesApi, 'getConversations')
              .where({ providerId })
              .items({ conversation_id: oldConversationId })
              .build(conversationUpdater),
          )
          dispatch(
            updateEndpointCache(messagesApi, 'getPatientConversations')
              .where({ providerId })
              .items({ conversation_id: oldConversationId })
              .build(conversationUpdater),
          )
          // Re-point the pair thread now so selection never falls back to the retired id.
          dispatch(
            updateEndpointCache(messagesApi, 'getProviderPairConversations')
              .where({ providerId })
              .items({ external_conversation_id: oldConversationId })
              .build((conversation) => {
                conversation.external_conversation_id = newConversationId
                conversation.status = CONVERSATION_STATE.ACTIVE
              }),
          )
          dispatch(updateSelectedConversationID({ oldConversationId, newConversationId }))
          refreshPairList = true
        }
        dispatch(
          updateEndpointCache(messagesApi, 'getMessages')
            .where({ conversationId: args.conversationId })
            .build((messages) => {
              messages.conversation_messages.push({
                body: args.data.message_body,
                sendStatus: 'sending',
                attributes: JSON.stringify({
                  from: args.data.message_author_id,
                  author_type: args.data.message_author_type,
                  message_type: args.data.message_type,
                  metadata: args.data.metadata,
                }),
                date_created: new Date().toISOString(),
                message_id: tempId,
              })
            }),
        )
        dispatch(
          updateEndpointCache(messagesApi, 'getLatestMessage')
            .where({ conversationId: args.conversationId })
            .build((messages) => {
              messages.conversation_messages = [
                {
                  body: args.data.message_body,
                  sendStatus: 'sending',
                  attributes: JSON.stringify({
                    from: args.data.message_author_id,
                    author_type: args.data.message_author_type,
                    message_type: args.data.message_type,
                    metadata: args.data.metadata,
                  }),
                  date_created: new Date().toISOString(),
                  message_id: tempId,
                },
              ]
            }),
        )
        try {
          const { data } = await queryFulfilled

          dispatch(
            updateEndpointCache(messagesApi, 'getMessages')
              .where({ conversationId: args.conversationId })
              .build((messages) => {
                const idx = messages.conversation_messages.findIndex((message) => message.message_id === tempId)
                if (idx !== -1) {
                  messages.conversation_messages[idx] = data
                }
              }),
          )
          dispatch(
            updateEndpointCache(messagesApi, 'getLatestMessage')
              .where({ conversationId: args.conversationId })
              .build((messages) => {
                const idx = messages.conversation_messages.findIndex((message) => message.message_id === tempId)
                if (idx !== -1) {
                  messages.conversation_messages[idx] = data
                }
              }),
          )

          if (data.new_conversation_id) {
            repointConversation(args.conversationId, data.new_conversation_id, true)
          }
        } catch (error) {
          // The backend may have replaced the conversation before the send failed.
          const replacementConversationId = getReplacementConversationId(error)
          if (replacementConversationId) {
            repointConversation(args.conversationId, replacementConversationId, false)
          }
          dispatch(
            updateEndpointCache(messagesApi, 'getMessages')
              .where({ conversationId: args.conversationId })
              .build((messages) => {
                const idx = messages.conversation_messages.findIndex((message) => message.message_id === tempId)
                if (idx !== -1) {
                  messages.conversation_messages[idx].sendStatus = 'failed'
                }
              }),
          )
          dispatch(
            updateEndpointCache(messagesApi, 'getLatestMessage')
              .where({ conversationId: args.conversationId })
              .build((messages) => {
                const idx = messages.conversation_messages.findIndex((message) => message.message_id === tempId)
                if (idx !== -1) {
                  messages.conversation_messages[idx].sendStatus = 'failed'
                }
              }),
          )
        } finally {
          // Refresh after sending, including failed first sends that leave an ordinary empty pair.
          if (refreshPairList) {
            // RTK Query 1.x drops invalidation refetches while a list is already loading.
            // Let any pre-send list response settle before requesting authoritative post-send state.
            await Promise.allSettled([...(pendingProviderPairLists.get(dispatch) ?? [])])
            dispatch(messagesApi.util.invalidateTags([RTKQueryTag.PROVIDER_PAIR_CONVERSATION]))
          }
        }
      },
    }),
    getMessage: builder.query<Message, GetMessageArgs>({
      query: ({ conversationId, messageId }) => ({
        method: 'get',
        url: `/lt/v1/messaging/conversations/${conversationId}/messages/${messageId}`,
      }),
      async onQueryStarted({ conversationId, append }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          if (!append) {
            return
          }
          dispatch(
            updateEndpointCache(messagesApi, 'getMessages')
              .where({ conversationId })
              .build((messages) => {
                messages.conversation_messages.push(data)
              }),
          )
        } catch {}
      },
    }),
    markProviderPairRead: builder.mutation<void, { conversationId: string }>({
      query: ({ conversationId }) => ({
        method: 'patch',
        url: `/lt/v1/messaging/conversations/${conversationId}/attributes`,
        data: { unread_provider_messages_count: 0 },
      }),
      async onQueryStarted({ conversationId }, { dispatch, getState, queryFulfilled }) {
        try {
          await queryFulfilled
          // RTK Query 1.x drops invalidation during an in-flight list request. As with sends,
          // wait before refreshing both lists; their current_user counts remain authoritative.
          await Promise.allSettled([...(pendingProviderPairLists.get(dispatch) ?? [])])
          // Decide only after settling: a list that started before the read can still show it unread.
          const showsUnread = Object.values(getState().api.queries).some(
            (query) =>
              query?.endpointName === 'getProviderPairConversations' &&
              (query.data as ProviderPairConversationObject[] | undefined)?.some(
                (conversation) =>
                  conversation.external_conversation_id === conversationId &&
                  (conversation.current_user.unread_count ?? 0) > 0,
              ),
          )
          if (showsUnread) {
            dispatch(messagesApi.util.invalidateTags([RTKQueryTag.PROVIDER_PAIR_CONVERSATION]))
          }
        } catch {}
      },
    }),
    updateConversationAttributes: builder.mutation<
      ConversationObject['conversation_attributes'],
      PatchConversationAttributesArgs
    >({
      query: ({
        conversationId,
        conversationType,
        isV2,
        unreadProviderMessagesCount,
        unreadAiCoachMessagesProviderCount,
      }) => ({
        method: 'patch',
        url: `/lt/${isV2 ? 'v2' : 'v1'}/messaging/conversations/${conversationId}/attributes`,
        // Pair read clears run in the background on open and on live messages, so a failure only logs.
        ...(conversationType === CONVERSATION_TYPE.PROVIDER_PROVIDER && { showAlert: false }),
        data: {
          unread_provider_messages_count: unreadProviderMessagesCount,
          ...(unreadAiCoachMessagesProviderCount !== undefined && {
            unread_ai_coach_messages_provider_count: unreadAiCoachMessagesProviderCount,
          }),
        },
      }),
      transformResponse: (response: ConversationObject['conversation_attributes'] | ConversationV2, _meta, { isV2 }) =>
        isV2
          ? mapConversationV2ToMessageConversation(response as ConversationV2).conversation_attributes
          : (response as ConversationObject['conversation_attributes']),
      async onQueryStarted(args, { dispatch, queryFulfilled, getState }) {
        const state = getState() as RootState
        const providerId = getAuthUserId(state)
        try {
          const { data } = await queryFulfilled
          if (args.conversationType === CONVERSATION_TYPE.PROVIDER_PROVIDER) {
            // Pair unread lives on current_user in the pair list, so refetch it once any in-flight list settles.
            await Promise.allSettled([...(pendingProviderPairLists.get(dispatch) ?? [])])
            dispatch(messagesApi.util.invalidateTags([RTKQueryTag.PROVIDER_PAIR_CONVERSATION]))
            return
          }
          // Merge instead of replace: the patch response only contains the updated
          // fields (e.g. unread counts), not the full conversation_attributes — so a
          // wholesale assignment was stripping the `type` field. That made the AI
          // coach conversation look like a regular one downstream (isAICoachSelected
          // = false), and the chat input stayed mounted on the AI coach tab.
          const updater = (conversation: ConversationObject) => {
            conversation.conversation_attributes = { ...conversation.conversation_attributes, ...data }
          }
          if (args.isV2) {
            dispatch(
              updateEndpointCache(messagesApi, 'getConversationsV2')
                .where({ providerId })
                .items({ conversation_id: args.conversationId })
                .build(updater),
            )
            dispatch(
              updateEndpointCache(messagesApi, 'getPatientConversationsV2')
                .where({ providerId })
                .items({ conversation_id: args.conversationId })
                .build(updater),
            )
          } else {
            dispatch(
              updateEndpointCache(messagesApi, 'getConversations')
                .where({ providerId })
                .items({ conversation_id: args.conversationId })
                .build(updater),
            )
            dispatch(
              updateEndpointCache(messagesApi, 'getPatientConversations')
                .where({ providerId })
                .items({ conversation_id: args.conversationId })
                .build(updater),
            )
          }
        } catch {}
      },
    }),
    sendOOOMessage: builder.mutation<void, PostOOOMessageArgs>({
      query: (data) => ({
        method: 'post',
        url: `/lt/v1/messaging/conversations/bulk_message`,
        data,
      }),
    }),
    getDrafts: builder.query<DraftObject[], GetDraftsArgs>({
      query: ({ providerId }) => ({
        method: 'get',
        url: `/lt/v1/messaging/conversations/drafts`,
        params: { provider_id: providerId },
      }),
      providesTags: [RTKQueryTag.DRAFT],
    }),
    deleteDraft: builder.mutation<void, DeleteDraftArgs>({
      query: ({ conversationId, draftId }) => ({
        method: 'delete',
        url: `/lt/v1/messaging/conversations/${conversationId}/drafts/${draftId}`,
      }),
      invalidatesTags: [RTKQueryTag.DRAFT],
    }),
    updateDraft: builder.mutation<void, PatchDraftsArgs>({
      query: ({ conversationId, draftId, data }) => ({
        method: 'patch',
        url: `/lt/v1/messaging/conversations/${conversationId}/drafts/${draftId}`,
        data,
      }),
      invalidatesTags: [RTKQueryTag.DRAFT],
    }),
    createDraft: builder.mutation<DraftObject, PostDraftArgs>({
      query: ({ conversationId, data }) => ({
        method: 'post',
        url: `/lt/v1/messaging/conversations/${conversationId}/drafts`,
        data,
      }),
      invalidatesTags: [RTKQueryTag.DRAFT],
    }),
    getTemplates: builder.query<any[], GetTemplatesArgs>({
      query: ({ providerId }) => ({
        method: 'get',
        url: `/lt/v1/messaging/templates`,
        params: { provider_id: providerId },
      }),
      providesTags: [RTKQueryTag.TEMPLATE],
    }),
    deleteTemplate: builder.mutation<void, { providerId: string; templateId: string }>({
      query: ({ templateId }) => ({
        method: 'delete',
        url: `/lt/v1/messaging/templates/${templateId}`,
      }),
      invalidatesTags: [RTKQueryTag.TEMPLATE],
    }),
    updateTemplate: builder.mutation<void, { providerId: string; templateId: string; data: any }>({
      query: ({ templateId, data }) => ({
        method: 'patch',
        url: `/lt/v1/messaging/templates/${templateId}`,
        data,
      }),
      invalidatesTags: [RTKQueryTag.TEMPLATE],
    }),
    addTemplate: builder.mutation<void, { providerId: string; data: any }>({
      query: ({ providerId, data }) => ({
        method: 'post',
        url: `/lt/v1/messaging/templates`,
        data: { ...data, provider_id: providerId },
      }),
      invalidatesTags: [RTKQueryTag.TEMPLATE],
    }),
    getAIDraftsBulk: builder.query<AIDraft[], GetAIDraftsBulkArgs>({
      query: ({ promptType, statusOnly, providerId }) => ({
        method: 'get',
        url: `/lt/v1/messaging/conversations/ai/drafts`,
        params: { prompt_type: promptType, status_only: statusOnly, provider_id: providerId },
      }),
      providesTags: [RTKQueryTag.AI_DRAFT],
    }),
    getAIDraft: builder.query<AIDraft, GetAIDraftArgs>({
      query: ({ providerId, episodeId, sessionNumber, clientId }) => ({
        method: 'get',
        url: `/lt/v1/messaging/conversations/ai/draft`,
        params: { episode_id: episodeId, session_number: sessionNumber, provider_id: providerId, patient_id: clientId },
      }),
      providesTags: [RTKQueryTag.AI_DRAFT],
    }),
    patchAIDraft: builder.mutation<AIDraft, PatchAIDraftArgs>({
      query: ({ providerId, draftId, status }) => ({
        method: 'patch',
        url: `/lt/v1/messaging/conversations/ai/draft/tracker/${draftId}`,
        data: {
          status,
          provider_id: providerId,
        },
      }),
      invalidatesTags: [RTKQueryTag.AI_DRAFT],
    }),
    createConversation: builder.mutation<CreateConversationResponse, CreateConversationArgs>({
      query: (args) => ({
        method: 'post',
        url: `/lt/v1/messaging/conversations/participants`,
        data: buildCreateConversationRequestData(args),
      }),
      invalidatesTags: [RTKQueryTag.CONVERSATION],
    }),
    ensureProviderPairConversation: builder.mutation<ProviderPairCreateResponse, EnsureProviderPairConversationArgs>({
      query: buildProviderPairEnsureRequest,
      transformResponse: parseProviderPairCreateResponse,
      // The first send refreshes the list after the message settles. Refreshing here can
      // race that send and return an empty pair with stale capabilities.
    }),
    patchConversation: builder.mutation<void, PatchConversationArgs>({
      query: ({ conversationId, status }) => ({
        method: 'patch',
        // Status updates still use V1 with the Twilio conversation ID.
        // isV2 only determines which conversation cache is updated.
        url: `/lt/v1/messaging/conversations/${conversationId}`,
        data: {
          status,
        },
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        let oldStatus: CONVERSATION_STATE | undefined = undefined
        const cacheConversationId = args.cacheConversationId ?? args.conversationId
        const updateStatus = (conversation: ConversationObject) => {
          oldStatus = conversation.conversation_state
          conversation.conversation_state = args.status
        }
        const restoreStatus = (conversation: ConversationObject) => {
          conversation.conversation_state = oldStatus
        }
        let oldV2PatientStatus: CONVERSATION_STATE | undefined = undefined
        const updateV2PatientStatus = (conversation: ConversationObject) => {
          oldV2PatientStatus = conversation.conversation_state
          conversation.conversation_state = args.status
        }
        const restoreV2PatientStatus = (conversation: ConversationObject) => {
          conversation.conversation_state = oldV2PatientStatus
        }
        let oldV2ProviderStatus: CONVERSATION_STATE | undefined = undefined
        const updateV2ProviderStatus = (conversation: ConversationObject) => {
          oldV2ProviderStatus = conversation.conversation_state
          conversation.conversation_state = args.status
        }
        const restoreV2ProviderStatus = (conversation: ConversationObject) => {
          conversation.conversation_state = oldV2ProviderStatus
        }
        try {
          if (args.isV2) {
            dispatch(
              updateEndpointCache(messagesApi, 'getPatientConversationsV2')
                .where({ providerId: args.providerId, patientId: args.patientId })
                .items({ conversation_id: cacheConversationId })
                .build(updateV2PatientStatus),
            )
            dispatch(
              updateEndpointCache(messagesApi, 'getConversationsV2')
                .where({ providerId: args.providerId })
                .items({ conversation_id: cacheConversationId })
                .build(updateV2ProviderStatus),
            )
          } else {
            dispatch(
              updateEndpointCache(messagesApi, 'getPatientConversations')
                .where({ providerId: args.providerId, patientId: args.patientId })
                .items({ conversation_id: cacheConversationId })
                .build(updateStatus),
            )
          }
          await queryFulfilled
        } catch {
          if (args.isV2) {
            dispatch(
              updateEndpointCache(messagesApi, 'getPatientConversationsV2')
                .where({ providerId: args.providerId, patientId: args.patientId })
                .items({ conversation_id: cacheConversationId })
                .build(restoreV2PatientStatus),
            )
            dispatch(
              updateEndpointCache(messagesApi, 'getConversationsV2')
                .where({ providerId: args.providerId })
                .items({ conversation_id: cacheConversationId })
                .build(restoreV2ProviderStatus),
            )
          } else {
            dispatch(
              updateEndpointCache(messagesApi, 'getPatientConversations')
                .where({ providerId: args.providerId, patientId: args.patientId })
                .items({ conversation_id: cacheConversationId })
                .build(restoreStatus),
            )
          }
        }
      },
      invalidatesTags: [RTKQueryTag.CONVERSATION],
    }),
  }),
})

export const {
  useGetConversationsQuery,
  useGetConversationsV2Query,
  useGetPatientConversationsQuery,
  useGetPatientConversationsV2Query,
  useGetProviderPairConversationsQuery,
  useGetProviderPairCareHistoryQuery,
  useGetAccessTokenQuery,
  useGetMessagesQuery,
  useLazyGetMessagesQuery,
  useGetMessagesV2Query,
  useLazyGetMessagesV2Query,
  useSendMessageMutation,
  useLazyGetMessageQuery,
  useUpdateConversationAttributesMutation,
  useMarkProviderPairReadMutation,
  useSendOOOMessageMutation,
  useGetDraftsQuery,
  useDeleteDraftMutation,
  useUpdateDraftMutation,
  useCreateDraftMutation,
  useGetTemplatesQuery,
  useDeleteTemplateMutation,
  useUpdateTemplateMutation,
  useGetAIDraftQuery,
  useAddTemplateMutation,
  useGetAIDraftsBulkQuery,
  usePatchAIDraftMutation,
  usePatchConversationMutation,
  useCreateConversationMutation,
  useEnsureProviderPairConversationMutation,
  useGetLatestMessageQuery,
} = messagesApi

// Use the middleware dispatch shared with query lifecycles to find this store's pending lists.
export const refreshProviderPairConversations = () => async (dispatch: AppDispatch) => {
  await Promise.allSettled([...(pendingProviderPairLists.get(dispatch) ?? [])])
  dispatch(messagesApi.util.invalidateTags([RTKQueryTag.PROVIDER_PAIR_CONVERSATION]))
}

export default messagesApi
